import { DEFAULT_SEED_MAX, STORAGE_KEY_SETTINGS, WHEEL_MATCH_OFFERED } from '../constants';
import { type CasterId, isCasterList } from '../content/casters';
import { DEFAULT_MATCH_MODE, isMatchMode, type MatchMode } from '../game/state';
import { readJson, writeJson } from '../lib/storage';
import { createStore } from '../lib/store';

export type Settings = {
  seed: number;
  /** The mode of the next match. A match in progress keeps its own. */
  mode: MatchMode;
  /** The casters the player picked last, for a match of Godsmode. Null draws them. */
  casters: readonly CasterId[] | null;
  /** False when the player turned the sound off. */
  sound: boolean;
  /** True when each square of the board shows its name. */
  squareNames: boolean;
};

export const randomSeed = (): number => Math.floor(Math.random() * DEFAULT_SEED_MAX) + 1;

const stored = readJson<Partial<Settings>>(STORAGE_KEY_SETTINGS, {});

/** While no match with a wheel is offered, a stored mode has no effect and every new match has casters. */
const storedMode = (): MatchMode => {
  if (!WHEEL_MATCH_OFFERED) return 'gods';
  return isMatchMode(stored.mode) ? stored.mode : DEFAULT_MATCH_MODE;
};

export const settingsStore = createStore<Settings>({
  seed: typeof stored.seed === 'number' ? stored.seed : randomSeed(),
  mode: storedMode(),
  casters: isCasterList(stored.casters) ? stored.casters : null,
  sound: typeof stored.sound === 'boolean' ? stored.sound : true,
  squareNames: typeof stored.squareNames === 'boolean' ? stored.squareNames : false,
});

settingsStore.subscribe((settings) => writeJson(STORAGE_KEY_SETTINGS, settings));

export const setMode = (mode: MatchMode): void => settingsStore.set((s) => ({ ...s, mode }));
export const setCasters = (casters: readonly CasterId[] | null): void =>
  settingsStore.set((s) => ({ ...s, casters }));
export const setSeed = (seed: number): void => settingsStore.set((s) => ({ ...s, seed }));
export const setSound = (sound: boolean): void => settingsStore.set((s) => ({ ...s, sound }));
export const setSquareNames = (squareNames: boolean): void =>
  settingsStore.set((s) => ({ ...s, squareNames }));
